import {
  Autocomplete,
  Chip,
  DialogActions,
  DialogContent,
  MenuItem,
} from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import breakpoints from '../../../design-system/breakpoints';
import OvTextField from '../atoms/OvTextField';
import Colours from '../../../design-system/colours';
import { Field, FieldProps } from 'formik';
import OvQuillEditor from '../atoms/OvQuillEditor';
import Variables from '../../../design-system/variables';
import OvSelect from '../atoms/OvSelect';
import { NotificationContentCategory } from '../../../common/model/dto/notification/notification-content';
import OvImageUpload from './OvImageUpload';
import {
  OOVA_NOTIFICATION_CONTENT_IMAGES,
  OOVA_NOTIFICATION_CONTENT_THUMBNAIL_IMAGES,
} from '../../../api/firebase-storage';
import OvButton from '../atoms/OvButton';

interface OvNotificationContentInfoStepProps {
  formik: any;
  onNext: () => void;
  onCancel: () => void;
  onImageUpload: ({
    image_path,
    resized_image_path,
  }: {
    image_path: string;
    resized_image_path: string;
  }) => void;
}

const OvNotificationContentInfoStep: FC<OvNotificationContentInfoStepProps> = ({
  formik,
  onNext,
  onCancel,
  onImageUpload,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  return (
    <form>
      <StyledDialogContent>
        <StyledTextField
          type="text"
          fullWidth
          autoComplete="off"
          label={t('notificationContents.fields.title')}
          error={formik.touched.title && !!formik.errors.title}
          helperText={
            formik.errors.title && formik.touched.title && formik.errors.title
          }
          {...formik.getFieldProps('title')}
        />
        <StyledTextField
          type="text"
          fullWidth
          autoComplete="off"
          label={t('notificationContents.fields.shortDescription')}
          error={
            formik.touched.short_description &&
            !!formik.errors.short_description
          }
          helperText={
            formik.errors.short_description &&
            formik.touched.short_description &&
            formik.errors.short_description
          }
          {...formik.getFieldProps('short_description')}
        />
        <Field type="text" name="full_description">
          {({ field }: FieldProps) => (
            <StyledOvQuillEditor
              value={field.value}
              handleChange={field.onChange(field.name)}
              placeholder={t('notificationContents.fields.fullDescription')}
            />
          )}
        </Field>
        <StyledTextField
          type="text"
          fullWidth
          autoComplete="off"
          label={t('notificationContents.fields.disclaimer')}
          error={formik.touched.disclaimer && !!formik.errors.disclaimer}
          helperText={
            formik.errors.disclaimer &&
            formik.touched.disclaimer &&
            formik.errors.disclaimer
          }
          {...formik.getFieldProps('disclaimer')}
        />
        <StyledAutocomplete
          multiple
          freeSolo
          options={[]}
          value={formik.values.tags || []}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(event, newValue) => {
            formik.setFieldValue('tags', newValue);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option as string} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={t('notificationContents.addTags')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && inputValue.trim() !== '') {
                  event.preventDefault();
                  if (
                    formik.values.tags &&
                    !formik.values.tags.includes(inputValue.trim())
                  ) {
                    formik.setFieldValue('tags', [
                      ...formik.values.tags,
                      inputValue.trim(),
                    ]);
                  }
                  setInputValue('');
                }
              }}
            />
          )}
        />
        <StyledLabel>{t('notificationContents.fields.category')}</StyledLabel>
        <StyledSelect
          labelId="health-goal"
          {...formik.getFieldProps('category')}
          value={formik.values.category}
        >
          {Object.values(NotificationContentCategory).map((category) => (
            <StyledMenuItem key={category} value={category}>
              {category}
            </StyledMenuItem>
          ))}
        </StyledSelect>

        {formik.values.category === NotificationContentCategory.APP_UPDATES && (
          <StyledTextField
            type="text"
            fullWidth
            autoComplete="off"
            label={t('notificationContents.fields.appVersion')}
            error={formik.touched.app_version && !!formik.errors.app_version}
            helperText={
              formik.errors.app_version &&
              formik.touched.app_version &&
              formik.errors.app_version
            }
            {...formik.getFieldProps('app_version')}
          />
        )}

        <OvImageUpload
          imageBasePath={OOVA_NOTIFICATION_CONTENT_IMAGES}
          imageUrl={formik.values.image_url}
          ctaLabel={t('imageUpload.uploadImage')}
          dialogTitle={t('notificationContents.dialogs.image.title')}
          hintText={t('notificationContents.dialogs.image.hint')}
          handleChange={onImageUpload}
          resizeOptions={{
            maxWidth: 150,
            maxHeight: 150,
            quailty: 1,
            resizedImagePath: OOVA_NOTIFICATION_CONTENT_THUMBNAIL_IMAGES,
          }}
        />

        <StyledDialogActions>
          <StyledLightOvButton onClick={onCancel}>
            {t('common.actions.cancel')}
          </StyledLightOvButton>
          <StyledOvButton
            onClick={onNext}
            disabled={!formik.values.title || !formik.values.short_description}
          >
            {t('common.actions.next')}
          </StyledOvButton>
        </StyledDialogActions>
      </StyledDialogContent>
    </form>
  );
};

export default OvNotificationContentInfoStep;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledTextField = styled(OvTextField)`
  && {
    flex: 1 0 100%;

    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }
    .MuiInputLabel-formControl {
      top: -0.2rem;
      opacity: 0.6;
    }
  }
`;

const StyledOvQuillEditor = styled(OvQuillEditor)`
  margin-bottom: 0.75rem;
`;

const StyledAutocomplete = styled(Autocomplete)`
  && {
    .MuiFormControl-root,
    .MuiInputLabel-formControl {
      font-size: 0.875rem;
    }

    .MuiInputBase-formControl {
      font-size: 0.75rem;
      background: ${Colours.WHITE};
      padding-top: 0;
      padding-bottom: 0;
      padding-left: 0;
    }
  }
`;

const StyledLabel = styled.label`
  font-size: ${Variables.fontSizes.MEDIUM};
  margin-right: 0.5rem;
`;

const StyledSelect = styled(OvSelect)`
  && {
    max-width: none;
    height: 3rem;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      width: 100%;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &.MuiButtonBase-root {
      display: flex;
      justify-content: flex-start;
      padding: 0.375rem 1rem;
      font-size: ${Variables.fontSizes.MEDIUM};
    }
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    max-width: fit-content;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem;
    max-width: fit-content;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;
